import React, { useMemo } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Form,
  Row,
  Col,
  InputNumber,
  Select,
  Switch,
  Checkbox,
  Button,
  DatePicker,
  Divider,
} from "antd";
import GenericCard from "components/UI/GenericCard/GenericCard";
import DashedTooltip from "components/UI/DashedTooltip";

const { RangePicker } = DatePicker;

export const MAX_BACKTESTS = 300000;
export const MAX_COMBO_MODELS = 20;

const binomial = (n, k) => {
  if (k > n) return 0;
  let result = 1;
  for (let i = 1; i <= k; i++) result = (result * (n - i + 1)) / i;
  return Math.round(result);
};

// number of tags typed into a grid field, ignoring non-numbers
const numericTags = (values) =>
  [...new Set((values || []).map(Number).filter((v) => Number.isFinite(v)))];

export const countBacktests = ({ symbols, models, comboSizes, axes }) => {
  const sets = (comboSizes || []).reduce(
    (sum, size) => sum + binomial(models, size),
    0,
  );
  const grid =
    numericTags(axes.takeProfitPercent).length *
    numericTags(axes.stopLossPercent).length *
    numericTags(axes.daysOpenExitCount).length;
  return { sets, grid, total: symbols * sets * grid };
};

// typing "MU" should offer MU before TMUS: exact, then prefix, then the rest
const prefixFirst = (a, b, { searchValue }) => {
  const query = (searchValue || "").toUpperCase();
  const rank = (option) => {
    const label = String(option.label).toUpperCase();
    if (label === query) return 0;
    return label.startsWith(query) ? 1 : 2;
  };
  return rank(a) - rank(b) || String(a.label).localeCompare(String(b.label));
};

// per-axis value checks: typed tags that fail are dropped as they're entered
const GRID_AXES = {
  takeProfitPercent: {
    isValid: (v) => v > 0,
    message: "Enter positive numbers, e.g. 2, 3, 4",
  },
  stopLossPercent: {
    isValid: (v) => v <= 0,
    message: "Enter 0 or negative numbers, e.g. 0, -8, -12",
  },
  daysOpenExitCount: {
    isValid: (v) => Number.isInteger(v) && v >= 1,
    message: "Enter whole numbers of 1 or more, e.g. 8, 12",
  },
};

const isValidTag = (axis, tag) => {
  if (String(tag).trim() === "") return false;
  const value = Number(tag);
  return Number.isFinite(value) && GRID_AXES[axis].isValid(value);
};

const gridItemProps = (axis) => ({
  name: axis,
  normalize: (tags) => (tags || []).filter((tag) => isValidTag(axis, tag)),
  rules: [
    {
      validator: (_, tags) =>
        tags?.length && tags.every((tag) => isValidTag(axis, tag))
          ? Promise.resolve()
          : Promise.reject(new Error(GRID_AXES[axis].message)),
    },
  ],
});

const gridSelect = (placeholder) => (
  <Select
    mode="tags"
    tokenSeparators={[",", " "]}
    placeholder={placeholder}
    open={false}
    suffixIcon={null}
    style={{ width: "100%" }}
  />
);

const ExplorerSetup = ({
  form,
  initialValues,
  modelsList,
  symbolsList,
  isPending,
  onRun,
  onCancel,
}) => {
  const values = Form.useWatch([], form) || initialValues;
  const allSymbols = values.allSymbols;
  const allModels = values.allModels;
  const symbolCount = allSymbols
    ? symbolsList.length
    : values.symbols?.length || 0;
  const modelCount = allModels ? modelsList.length : values.modelIds?.length || 0;
  const combosAllowed = modelCount <= MAX_COMBO_MODELS;

  const count = useMemo(
    () =>
      countBacktests({
        symbols: symbolCount,
        models: modelCount,
        comboSizes: values.comboSizes,
        axes: {
          takeProfitPercent: values.takeProfitPercent,
          stopLossPercent: values.stopLossPercent,
          daysOpenExitCount: values.daysOpenExitCount,
        },
      }),
    [symbolCount, modelCount, values],
  );
  const overLimit = count.total > MAX_BACKTESTS;

  // keep the holdout split at 70% of the range when the range changes
  const onDatesChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) return;
    const span = dates[1].diff(dates[0], "day");
    form.setFieldValue("splitDate", dates[0].add(Math.round(span * 0.7), "day"));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onRun}
    >
      <GenericCard title="Run setup" className="table-card" maxHeight={2000}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Divider orientation="left">Universe</Divider>
            <Form.Item name="allSymbols" valuePropName="checked" noStyle>
              <Checkbox>All enabled symbols ({symbolsList.length})</Checkbox>
            </Form.Item>
            {!allSymbols && (
              <Form.Item name="symbols" className="mt-2">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Symbols"
                  filterSort={prefixFirst}
                  options={symbolsList.map((s) => ({
                    label: s.ticker,
                    value: s.ticker,
                  }))}
                  maxTagCount={12}
                />
              </Form.Item>
            )}

            <div className="mt-4" />
            <Form.Item name="allModels" valuePropName="checked" noStyle>
              <Checkbox>All daily models ({modelsList.length})</Checkbox>
            </Form.Item>
            {!allModels && (
              <Form.Item name="modelIds" className="mt-2">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Models"
                  optionFilterProp="label"
                  options={modelsList.map((m) => ({
                    label: m.name,
                    value: m._id,
                  }))}
                  maxTagCount={8}
                />
              </Form.Item>
            )}
            <Form.Item
              name="comboSizes"
              className="mt-3"
              label={
                <DashedTooltip
                  text={`Each set trades only when all of its models say buy (same consensus as AI Backtests). Pairs and triples need ${MAX_COMBO_MODELS} models or fewer.`}
                >
                  Model sets
                </DashedTooltip>
              }
            >
              <Checkbox.Group
                options={[
                  { label: "Single models", value: 1 },
                  { label: "Pairs", value: 2, disabled: !combosAllowed },
                  { label: "Triples", value: 3, disabled: !combosAllowed },
                ]}
              />
            </Form.Item>

            <Divider orientation="left">Period</Divider>
            <Row gutter={12}>
              <Col>
                <Form.Item name="dates" label="Backtest range">
                  <RangePicker
                    onChange={onDatesChange}
                    disabledDate={(d) => d.isAfter(dayjs())}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  name="splitDate"
                  label={
                    <DashedTooltip text="Rows also report the period from this date on separately (the holdout). Judge rows by the holdout: a result that was only good before the split is likely overfit. Defaults to 70% of the range.">
                      Holdout from
                    </DashedTooltip>
                  }
                >
                  <DatePicker />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} lg={12}>
            <Divider orientation="left">Parameter grid</Divider>
            <p className="mb-3" style={{ opacity: 0.7 }}>
              Type values separated by commas; every combination is tested.
            </p>
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item
                  {...gridItemProps("takeProfitPercent")}
                  label="Take profit %"
                >
                  {gridSelect("e.g. 2, 3, 4")}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  {...gridItemProps("stopLossPercent")}
                  label={
                    <DashedTooltip text="0 = no stop loss. Use negative values.">
                      Stop loss %
                    </DashedTooltip>
                  }
                >
                  {gridSelect("e.g. 0, -8, -12")}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  {...gridItemProps("daysOpenExitCount")}
                  label="Bars open exit"
                >
                  {gridSelect("e.g. 8, 12")}
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Sizing and rules</Divider>
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="initialBalance" label="Initial balance">
                  <InputNumber min={1} prefix="$" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Order size">
                  <Row gutter={4} wrap={false}>
                    <Col flex="auto">
                      <Form.Item name="orderSize" noStyle>
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item name="orderSizeType" noStyle>
                        <Select
                          style={{ width: 110 }}
                          options={[
                            { label: "% equity", value: "percent" },
                            { label: "USD", value: "USD" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="maxOpenOrders" label="Max open orders">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="userCommission" label="Min commission">
                  <InputNumber min={0} prefix="$" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="premiumPerShare" label="Commission / share">
                  <InputNumber
                    min={0}
                    step={0.001}
                    prefix="$"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="slippage" label="Slippage ($/share)">
                  <InputNumber
                    min={0}
                    step={0.01}
                    prefix="$"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="enterPositionBuyCount"
                  label="Buy signals to enter"
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="exitPositionNoBuyCount"
                  label={
                    <DashedTooltip text="Exit after this many no-buy signals in a row. 0 = off.">
                      No-buy exit
                    </DashedTooltip>
                  }
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="minTrades"
                  label={
                    <DashedTooltip text="Rows with fewer trades are tested but not shown -- too few trades is noise.">
                      Min trades shown
                    </DashedTooltip>
                  }
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="resetTrendAfterSell"
                  label="Reset buy streak after sell"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="excludeCorrupted"
                  valuePropName="checked"
                  label={
                    <DashedTooltip text="Skip model sets with a model that was trained on the symbol during the backtest range -- those results are biased.">
                      Skip biased models
                    </DashedTooltip>
                  }
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <div className="flex items-center gap-4 flex-wrap mt-2">
          {isPending ? (
            <Button danger onClick={onCancel}>
              Stop
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              disabled={!count.total || overLimit}
            >
              Run {count.total ? count.total.toLocaleString() : ""} backtests
            </Button>
          )}
          <span style={{ opacity: 0.8 }}>
            {symbolCount} symbols × {count.sets.toLocaleString()} model sets ×{" "}
            {count.grid} parameter sets
          </span>
          {overLimit && (
            <span className="red">
              Over the {MAX_BACKTESTS.toLocaleString()} limit -- narrow the run
            </span>
          )}
        </div>
      </GenericCard>
    </Form>
  );
};

ExplorerSetup.propTypes = {
  form: PropTypes.object,
  initialValues: PropTypes.object,
  modelsList: PropTypes.array,
  symbolsList: PropTypes.array,
  isPending: PropTypes.bool,
  onRun: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ExplorerSetup;
